/**
 * DeleteCommentModal Component
 *
 * Story BL-024.3: Upvote/Downvote Refinements + Moderation Features
 * Task 8: Create DeleteCommentModal component
 *
 * Confirmation dialog for deleting comments. Uses a React modal (NOT window.confirm)
 * for moderator deletes. Follows the RoleAssignConfirmModal pattern.
 *
 * @see AC6 - Moderator delete confirmation must use a proper modal dialog component
 *
 * @module components/governance/DeleteCommentModal
 */

import { useEffect, useCallback } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface DeleteCommentModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Display name of the comment author */
  authorDisplayName: string;
  /** True when a mod/admin is deleting another user's comment */
  isModerating: boolean;
  /** Called when the user confirms deletion */
  onConfirm: () => void;
  /** Called when the user cancels */
  onCancel: () => void;
  /** Whether the delete operation is in progress */
  isSubmitting?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function DeleteCommentModal({
  visible,
  authorDisplayName,
  isModerating,
  onConfirm,
  onCancel,
  isSubmitting,
}: DeleteCommentModalProps) {
  // Handle Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [visible, handleKeyDown]);

  if (!visible) return null;

  const title = 'Delete Comment';
  const body = isModerating
    ? `Delete this comment by ${authorDisplayName}? This cannot be undone.`
    : 'Delete your comment? This cannot be undone.';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="delete-comment-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-comment-dialog-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
        <h2
          id="delete-comment-dialog-title"
          className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          {title}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">{body}</p>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
            data-testid="delete-modal-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
            data-testid="delete-modal-confirm-btn"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteCommentModal;
